import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Pagination,
  Skeleton,
  Chip,
  useTheme,
  Divider,
  Button,
} from '@mui/material';
import {
  Search,
  Close as CloseIcon,
  ArrowBack,
  PictureAsPdf,
  InsertDriveFile,
  FolderOpen,
  Download as DownloadIcon,
  // ChevronRight,
} from '@mui/icons-material';
import { apiGet } from '@/services/api/apiClient';
import { API_ENDPOINTS } from '@/constants/endpoints';
import FeedMedia from '@/components/dashboard/feed/FeedMedia';
import { CanvasPdfViewer } from '@/components/dashboard/feed/CanvasPdfViewer';
import { Dialog, DialogTitle, DialogContent } from '@mui/material';

interface DocumentItem {
  _id: string;
  mediaFileId?: string;
  type?: string;
  url: string;
  fileName?: string;
  contentType?: string;
  thumbnail?: string;
  size?: number;
  // FeedMedia compatibility fields
  mimeType?: string;
  originalName?: string;
}

interface FeedWithDocuments {
  _id: string;
  title: string;
  documentDetails: DocumentItem[];
}

interface PostsWithDocumentsResponse {
  success: boolean;
  data: {
    feeds: FeedWithDocuments[];
    pagination: {
      current: number;
      pages: number;
      total: number;
      limit: number;
    };
  };
}

// const formatSize = (bytes?: number): string => {
//   if (!bytes) return '';
//   if (bytes < 1024) return `${bytes} B`;
//   if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
//   return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
// };

const isPdfDocument = (doc: DocumentItem): boolean => {
  const ct = (doc.contentType || doc.mimeType || '').toLowerCase();
  return ct.includes('pdf') || (doc.fileName || '').toLowerCase().endsWith('.pdf');
};

export const DocumentExplorerPage: React.FC = () => {
  const theme = useTheme();

  const [feeds, setFeeds] = useState<FeedWithDocuments[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [activeFeed, setActiveFeed] = useState<FeedWithDocuments | null>(null);
  const [previewDoc, setPreviewDoc] = useState<DocumentItem | null>(null);

  const limit = 10;

  const fetchPostsWithDocuments = async () => {
    setLoading(true);
    try {
      const sp = new URLSearchParams();
      sp.append('page', page.toString());
      sp.append('limit', limit.toString());
      if (search) sp.append('search', search);
      const response = await apiGet<PostsWithDocumentsResponse>(
        `${API_ENDPOINTS.FEED.POSTS_WITH_DOCUMENTS}?${sp.toString()}`
      );
      if (response.success) {
        setFeeds(response.data.feeds || []);
        setTotalPages(response.data.pagination.pages || 1);
        setTotalItems(response.data.pagination.total || 0);
      }
    } catch (err) {
      console.error('Failed to load documents:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPostsWithDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search]);

  const handleSearchSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  // ── List View ─────────────────────────────────────────────
  const renderListView = () => (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <FolderOpen color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Document Explorer
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Browse posts that include documents. Click any post to preview attached files.
      </Typography>

      {/* Search */}
      <Box component="form" onSubmit={handleSearchSubmit} sx={{ mb: 3 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by post title or file name…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={clearSearch}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rectangular" height={92} sx={{ borderRadius: 2 }} />
          ))}
        </Box>
      ) : feeds.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 10,
            border: `1px dashed ${theme.palette.divider}`,
            borderRadius: 2,
            bgcolor: 'background.paper',
          }}
        >
          <FolderOpen sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
          <Typography variant="h6" color="text.secondary">
            No documents found
          </Typography>
          <Typography variant="body2" color="text.disabled">
            {search ? 'Try a different search term.' : 'Posts with attached documents will appear here.'}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {feeds.map((feed) => {
            // const docCount = feed.documentDetails?.length || 0;
            return (
              <Box
                key={feed._id}
                onClick={() => setActiveFeed(feed)}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  bgcolor: 'background.paper',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: theme.shadows[2],
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 1.5,
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      opacity: 0.9,
                    }}
                  >
                    <FolderOpen sx={{ fontSize: 22 }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 700, lineHeight: 1.3, mb: 0.75 }}
                      noWrap
                    >
                      {feed.title || 'Untitled post'}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      {(feed.documentDetails || []).map((doc) => (
                        <Chip
                          key={doc._id}
                          icon={
                            isPdfDocument(doc) ? (
                              <PictureAsPdf sx={{ fontSize: 14 }} />
                            ) : (
                              <InsertDriveFile sx={{ fontSize: 14 }} />
                            )
                          }
                          label={doc.fileName || 'Document'}
                          size="small"
                          variant="outlined"
                          sx={{
                            height: 24,
                            fontSize: '0.72rem',
                            maxWidth: 280,
                            '& .MuiChip-label': { px: 1 },
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                  {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      {docCount} file{docCount === 1 ? '' : 's'}
                    </Typography>
                    <ChevronRight sx={{ color: 'text.secondary' }} />
                  </Box> */}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Pagination */}
      {feeds.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            mt: 3,
            pt: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing <strong>{(page - 1) * limit + 1}</strong>–
            <strong>{Math.min(page * limit, totalItems)}</strong> of <strong>{totalItems}</strong>{' '}
            {totalItems === 1 ? 'document' : 'documents'}
          </Typography>
          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              shape="rounded"
              siblingCount={1}
              boundaryCount={1}
            />
          )}
        </Box>
      )}
    </>
  );

  // ── Detail View ───────────────────────────────────────────
  const detailMedia = useMemo(() => {
    if (!activeFeed) return [];
    return (activeFeed.documentDetails || []).map((doc) => ({
      ...doc,
      type: doc.type || 'document',
      mimeType: doc.contentType || doc.mimeType || '',
      originalName: doc.fileName || doc.originalName || 'Document',
      url: doc.url,
      size: doc.size || 0,
    })) as any;
  }, [activeFeed]);

  const handleDownloadDocument = async (doc: DocumentItem) => {
    try {
      const response = await fetch(doc.url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = doc.fileName || doc.originalName || 'document';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Failed to download document:', err);
      window.open(doc.url, '_blank');
    }
  };

  const handleDownloadCurrent = async () => {
    if (!activeFeed) return;
    const doc = activeFeed.documentDetails?.[0];
    if (!doc) return;
    await handleDownloadDocument(doc);
  };

  const renderDetailView = () => {
    if (!activeFeed) return null;
    return (
      <>
          <Button
                startIcon={<ArrowBack />}
              onClick={() => setActiveFeed(null)}
                sx={{ mb: 2, textTransform: 'none' }}
              >
                Back to Documents
              </Button>
        {/* <IconButton
          onClick={() => setActiveFeed(null)}
          sx={{ mb: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}
        >
          <ArrowBack fontSize="small" />
          <Typography variant="body2" sx={{ ml: 1, fontWeight: 600, pr: 1 }}>
            Back to list
          </Typography>
        </IconButton> */}

        <Box
          sx={{
            p: { xs: 2.5, md: 3 },
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: 'background.paper',
            boxShadow: theme.shadows[1],
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                letterSpacing: -0.5,
                fontSize: { xs: '1.4rem', sm: '1.8rem' },
                flex: 1,
                minWidth: 0,
              }}
              noWrap
            >
              {activeFeed.title}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadCurrent}
              disabled={!activeFeed.documentDetails?.length}
              sx={{ textTransform: 'none', flexShrink: 0 }}
            >
              Download
            </Button>
          </Box>

          {/* <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
            {(activeFeed.documentDetails || []).map((doc) => (
              <Chip
                key={doc._id}
                icon={
                  isPdfDocument(doc) ? (
                    <PictureAsPdf sx={{ fontSize: 14 }} />
                  ) : (
                    <InsertDriveFile sx={{ fontSize: 14 }} />
                  )
                }
                label={`${doc.fileName || 'Document'}${doc.size ? ` · ${formatSize(doc.size)}` : ''}`}
                size="small"
                variant="outlined"
                onClick={() => setPreviewDoc(doc)}
                sx={{ cursor: 'pointer', fontSize: '0.72rem', height: 26 }}
              />
            ))}
          </Box> */}

          <Divider sx={{ mb: 2 }} />

          {/* Document previews via FeedMedia */}
          <FeedMedia
            media={detailMedia}
            onImageClick={() => {}}
            onDocumentClick={(url, fileName) => {
              const doc = activeFeed.documentDetails.find((d) => d.url === url) || {
                _id: url,
                url,
                fileName,
              };
              setPreviewDoc(doc as DocumentItem);
            }}
          />
        </Box>
      </>
    );
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', p: { xs: 2, md: 3 } }}>
      {loading && !feeds.length ? (
        <Box display="flex" justifyContent="center" py={10}>
          <CircularProgress />
        </Box>
      ) : activeFeed ? (
        renderDetailView()
      ) : (
        renderListView()
      )}

      {/* PDF Preview Dialog (when user explicitly clicks a chip) */}
      <Dialog
        open={!!previewDoc}
        onClose={() => setPreviewDoc(null)}
        maxWidth="lg"
        fullWidth
        slotProps={{ paper: { sx: { height: '90vh' } } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle1" fontWeight={700} noWrap>
            {previewDoc?.fileName || 'Document Preview'}
          </Typography>
          <IconButton size="small" onClick={() => setPreviewDoc(null)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {previewDoc && isPdfDocument(previewDoc) ? (
            <CanvasPdfViewer url={previewDoc.url} fileName={previewDoc.fileName} />
          ) : previewDoc ? (
            <Box
              component="iframe"
              src={`https://docs.google.com/viewer?url=${encodeURIComponent(previewDoc.url)}&embedded=true`}
              sx={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
              title={previewDoc.fileName || 'Document'}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DocumentExplorerPage;
